import * as React from "react"
import { FC, useCallback, useMemo, useRef, useState } from "react"
import * as yup from "yup"
import moment from "moment"
import { Appearance, TextInput, ViewStyle, TouchableOpacity, TextStyle } from "react-native"
import { DateTimePickerField } from "../../field/date-time-picker-field/date-time-picker-field"
import { FormProps } from "../form"
import { Formik } from "formik"
import { NumeralField } from "../../field/numeral-field/numeral-field"
import { Text } from "../../text/text"
import { TextField } from "../../field/text-field/text-field"
import { View } from "native-base"
import { color } from "../../../theme"
import { observer } from "mobx-react-lite"
import { translate } from "../../../i18n"
import { JalaIcon } from "../../jala-icon/jala-icon"
import numeral from "numeral"
import _ from "lodash"

const HIDDEN_BUTTON: ViewStyle = {
  paddingHorizontal: 16,
  flexDirection: 'row',
  alignItems: 'center',
  paddingVertical: 10
}

const PLAN_TITLE: TextStyle = {
  marginHorizontal: 16,
  marginBottom: 14,
  marginTop: 5
}

const PLAN_TITLE_CONTAINER: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center',
  paddingHorizontal: 16,
  justifyContent: 'space-between',
  marginBottom: 10,
}

const PLAN_INPUT_CONTAINER: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center',
  paddingLeft: 16,
  justifyContent: 'space-between',
  marginBottom: 10
}

const PLAN_BUTTON = (isFull): ViewStyle => ({
  borderWidth: 1,
  borderColor: color.primary,
  borderRadius: 5,
  paddingVertical: 8,
  alignItems: 'center',
  marginHorizontal: 16,
  marginVertical: 8,
  opacity: isFull ? 0.5 : 1
})

const PLANT_EXAMPLE: ViewStyle = {
  paddingHorizontal: 8,
  paddingVertical: 10,
  backgroundColor: color.palette.gray200,
  borderWidth: 1,
  borderColor: color.palette.lineDark,
  borderRadius: 5,
}

export interface SimulationFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
  initialValues?: {
    name?: string,
    area?: number,
    date?: string,
    seed?: number,
    periode?: number,
    biomass?: number,
    survival?: number,
    size?: number,
    feed?: number,
    plan?: any,
  }
}

const SimulationExample: FC<{ values: { periode: any, plan: Array<any> } }> = observer(function SimulationHeader(props){
const { values } = props
  return (
    <View style={PLAN_INPUT_CONTAINER} marginRight="16px">
      <View width="16px" />
      <View style={PLANT_EXAMPLE} width="39%" marginRight="10px">
        <Text preset="body2" color={color.palette.gray800}>{values.periode}</Text>
      </View>
      <View style={PLANT_EXAMPLE} width="36%">
        <Text preset="body2" color={color.palette.gray800}>{numeral(100 - _.sumBy(values.plan, (p) => Number(p.percent))).format('0')}</Text>
      </View>
    </View>
  )
})

/**
 * Describe your component here
 */
export const SimulationForm = observer(function SimulationForm(props: SimulationFormProps) {
  const {
    initialValues: initialValuesProps = {}, onSubmit: onSubmitProps, colorScheme = Appearance.getColorScheme(), innerRef,
    ...rest
  } = props
  const [ planShow, setPlanShow ] = useState<boolean>(false)

  const input = {
    name: useRef<TextInput>(),
    area: useRef<TextInput>(),
    seed: useRef<TextInput>(),
    periode: useRef<TextInput>(),
    biomass: useRef<TextInput>(),
    survival: useRef<TextInput>(),
    size: useRef<TextInput>(),
    feed: useRef<TextInput>(),
    age: useRef<TextInput>(),
    percent: useRef<TextInput>(),
  }

  const nextInput = {
    quantity: () => input.area.current && input.area.current.focus(),
    area: () => input.seed.current && input.seed.current.focus(),
    seed: () => input.periode.current && input.periode.current.focus(),
    periode: () => input.biomass.current && input.biomass.current.focus(),
    biomass: () => input.survival.current && input.survival.current.focus(),
    survival: () => input.size.current && input.size.current.focus(),
    size: () => input.feed.current && input.feed.current.focus(),
  }

  const validationSchema = yup.object({
    name: yup.string().ascii().nullable().required().label(translate("simulationForm.name")),
    date: yup.string().nullable().required().label(translate("simulationForm.date")),
    area: yup.number().moreThan(0).max(10000).nullable().required().label(translate("simulationForm.area")),
    seed: yup.number().moreThan(0).max(1000000).nullable().required().label(translate("simulationForm.seed")),
    periode: yup.number().moreThan(0).max(150).nullable().required().label(translate("simulationForm.periode")),
    biomass: yup.number().moreThan(0).max(10).nullable().required().label(translate("simulationForm.biomass")),
    survival: yup.number().moreThan(0).max(100).nullable().required().label(translate("simulationForm.survival")),
    size: yup.number().min(34).max(50).nullable().required().label(translate("simulationForm.size")),
    feed: yup.number().moreThan(0).max(50000).nullable().required().label(translate("simulationForm.feed")),
  })

  const onSubmit = useCallback((values) => {
    onSubmitProps({ ...values })
  }, [onSubmitProps])

  const addPlan = useCallback((plan, setFieldValue) => {
    const newData = { age: '', percent: '' };
    setFieldValue('[plan]', [...plan, newData]);
  }, [])

  const onPressTrash = useCallback((index, setFieldValue, plan) => {
    const items = [...plan];
    if (index !== 0) {
      items.splice(index, 1);
    } else {
      let newData = items[index];
      newData = { age: '', percent: '' };
      items[index] = newData;
    }
    setFieldValue('[plan]', items);
  }, [])

  const initialValues = useMemo<SimulationFormProps["initialValues"]>(() => ({
    name: initialValuesProps.name || `Simulation ${moment().format('YYYY-MM-DD')}`,
    ...initialValuesProps,
    date: initialValuesProps.date || moment.utc().format("YYYY-MM-DD"),
    area: initialValuesProps.area || 1000,
    periode: initialValuesProps.periode || 120,
    biomass: initialValuesProps.biomass|| 1.2,
    survival: initialValuesProps.survival || 100,
    size: initialValuesProps.size || 45,
    feed: initialValuesProps.feed || 15000,
    plan: initialValuesProps.plan || [{ age: null, percent: null }],
    seed: initialValuesProps.seed || 100000,
  }), [initialValuesProps])

  const separatorIcon = planShow ? 'arrow_drop_up' : 'arrow_drop_down';


  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {({ values, setFieldValue }) => (
        <>
          <TextField
            name="name"
            txPrefix="simulationForm"
            colorScheme={colorScheme}
            input={input}
            nextInput={nextInput}
            formControlProps={{ isRequired: true, inputProps: { multiline: false, numberOfLines: 1, onSubmitEditing: undefined } }}
          />
          <NumeralField
            name="area"
            txPrefix="simulationForm"
            colorScheme={colorScheme}
            input={input}
            nextInput={nextInput}
            formControlProps={{ isRequired: true }}
          />
          <View flexDirection="row">
            <View flex={1}>
              <DateTimePickerField
                name="date"
                txPrefix="simulationForm"
                colorScheme={colorScheme}
                formControlProps={{ isRequired: true }}
                dateTimePickerProps={{ displayFormat: "DD MMM YYYY" }}
              />
            </View>
            <View flex={1}>
              <NumeralField
                name="seed"
                txPrefix="simulationForm"
                colorScheme={colorScheme}
                input={input}
                nextInput={nextInput}
                formControlProps={{ isRequired: true }}
              />
            </View>
          </View>
          <View flexDirection="row">
            <View flex={1}>
              <NumeralField
                name="periode"
                txPrefix="simulationForm"
                colorScheme={colorScheme}
                input={input}
                nextInput={nextInput}
                formControlProps={{ isRequired: true }}
              />
            </View>
            <View flex={1}>
              <NumeralField
                name="biomass"
                txPrefix="simulationForm"
                colorScheme={colorScheme}
                input={input}
                nextInput={nextInput}
                formControlProps={{ isRequired: true }}
              />
            </View>
          </View>
          <NumeralField
            name="survival"
            txPrefix="simulationForm"
            colorScheme={colorScheme}
            input={input}
            nextInput={nextInput}
            formControlProps={{ isRequired: true }}
          />
          <NumeralField
            name="size"
            txPrefix="simulationForm"
            colorScheme={colorScheme}
            input={input}
            nextInput={nextInput}
            formControlProps={{ isRequired: true }}
          />
          <NumeralField
            name="feed"
            txPrefix="simulationForm"
            colorScheme={colorScheme}
            input={input}
            nextInput={nextInput}
            formControlProps={{ isRequired: true }}
          />
          <TouchableOpacity style={HIDDEN_BUTTON} onPress={() => setPlanShow(!planShow)}>
            <Text preset="title4" color={color.primary} tx="simulationScreen.addPartial" />
            <JalaIcon name={separatorIcon} size={17} color={color.primary} />
          </TouchableOpacity>
          {planShow && (
            <View>
              <Text preset="title3" color={color.palette.gray800} tx="simulationScreen.partialPlan" style={PLAN_TITLE} />
              <View style={PLAN_TITLE_CONTAINER}>
                <View width="5%" />
                <View width="45%" alignItems="center">
                  <Text preset="title4" color={color.palette.darkGrey} tx="cycleHistoryScreen.age" />
                </View>
                <View width="40%" alignItems="center">
                  <Text preset="title4" color={color.palette.darkGrey} tx="simulationScreen.percent" />
                </View>
              </View>
              {values.plan.map((item, index) => {
                return (
                  <View key={index}>
                    <View style={PLAN_INPUT_CONTAINER}>
                      <TouchableOpacity onPress={() => onPressTrash(index, setFieldValue, values.plan)}>
                        <JalaIcon name="trash-1" color={color.primary} size={20} />
                      </TouchableOpacity>
                      <View width="45%">
                        <View flex={1}>
                          <NumeralField
                            name={`plan[${index}].age`}
                            txPrefix="simulationForm"
                            colorScheme={colorScheme}
                            input={input}
                            useLabel={false}
                          />
                        </View>
                      </View>
                      <View width="43%">
                        <View flex={1}>
                          <NumeralField
                            name={`plan[${index}].percent`}
                            txPrefix="simulationForm"
                            colorScheme={colorScheme}
                            input={input}
                            useLabel={false}
                          />
                        </View>
                      </View>
                    </View>
                  </View>
                )
              })}
              <SimulationExample values={values} />
              <TouchableOpacity
                style={PLAN_BUTTON(values.plan.reduce((prev, current) => prev + +current.percent, 0) >= 100)}
                onPress={() => addPlan(values.plan, setFieldValue)}
                disabled={values.plan.reduce((prev, current) => prev + +current.percent, 0) >= 100}
              >
                <Text preset="display6" color={color.primary} tx="simulationScreen.addPlan" />
              </TouchableOpacity>
            </View>
          )}
        </>
      )}
    </Formik>
  )
})

SimulationForm.displayName = 'SimulationForm'
